import {
  BarChart2,
  RotateCcw,
  Target,
  ThumbsDown,
  ThumbsUp,
  Trophy,
} from 'lucide-react';

import { Button as BaseButton, type ButtonProps } from '@/components/button';
import { cn } from '@/lib/utils';

import type { QuizPlayerEndProps } from '../types';
import { QuizPlayerScreen } from './quiz-player-screen';
import { useTranslation } from '@/providers/i18n';

type QuizPlayerEndScreenProps = React.ComponentProps<'div'> &
  QuizPlayerEndProps & {
    slots?: { Button?: React.ElementType<ButtonProps> };
    slotProps?: { button?: ButtonProps };
  };

export function QuizPlayerEndScreen({
  score = 0,
  totalQuestions = 0,
  stats = {},
  onRestart,
  onFeedback = () => {},
  slots = {},
  slotProps,
}: QuizPlayerEndScreenProps) {
  const { t } = useTranslation();
  const { Button = BaseButton } = slots;

  return (
    <QuizPlayerScreen variant="end">
      <div className="text-center mb-8">
        <Trophy size={48} className="mx-auto mb-4 text-yellow-500" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('quizEnd', 'Quiz completato!')}
        </h2>
        <p className="text-muted-foreground">
          {t('quizEndDescription', 'Ecco come sei andato')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-muted p-4 rounded-lg text-center">
          <Trophy size={24} className="mx-auto mb-2 text-foreground" />
          <div className="text-2xl font-bold text-foreground">
            {score} / {totalQuestions}
          </div>
          <div className="text-sm text-foreground">
            {t('currentScore', 'Punteggio ottenuto')}
          </div>
        </div>

        <div className="border-2 border-muted p-4 rounded-lg text-center">
          <Target size={24} className="mx-auto mb-2 text-muted-foreground" />
          <div className="text-2xl font-bold text-muted-foreground">
            {stats.totalAttempts || 0}
          </div>
          <div className="text-sm text-muted-foreground">
            {t('quizAttempts', 'Tentativi')}
          </div>
        </div>

        <div className="border-2 border-muted p-4 rounded-lg text-center">
          <BarChart2 size={24} className="mx-auto mb-2 text-muted-foreground" />
          <div className="text-2xl font-bold text-muted-foreground">
            {stats.avgScore || 0} / {totalQuestions}
          </div>
          <div className="text-sm text-muted-foreground">
            {t('avgScore', 'Punteggio medio')}
          </div>
        </div>
      </div>

      <div className="space-y-4 max-w-xs mx-auto">
        <Button
          {...slotProps?.button}
          size="lg"
          variant="brand"
          className={cn('w-full shadow-none', slotProps?.button?.className)}
          onClick={onRestart}
        >
          <RotateCcw /> {t('quizRestart', 'Riprova')}
        </Button>

        <div className="flex gap-4">
          <Button
            {...slotProps?.button}
            className={cn(
              'flex-1 bg-success/10 text-success hover:bg-success/20 shadow-none',
              slotProps?.button?.className
            )}
            onClick={() => onFeedback(1)}
          >
            <ThumbsUp /> {t('helpful', 'Utile')}
          </Button>
          <Button
            {...slotProps?.button}
            className={cn(
              'flex-1 bg-destructive/10 text-destructive hover:bg-destructive/20 shadow-none',
              slotProps?.button?.className
            )}
            onClick={() => onFeedback(-1)}
          >
            <ThumbsDown size={20} /> {t('notHelpful', 'Non utile')}
          </Button>
        </div>
      </div>
    </QuizPlayerScreen>
  );
}
